/**
 * Migration 245 — „das ist er" und „keiner davon", für beide Quellen.
 *
 * Warum eine eigene Datei und nicht drei `apiMutate`-Aufrufe in der Komponente: Die vier Wege
 * (Register wählen / Register verwerfen / Places wählen / Places verwerfen) sind derselbe Vorgang
 * an zwei Ablagen. Sie teilen die Version (`lookupId` + `lookedUpAt`), den Fehlerfall, der keiner
 * ist („inzwischen neu abgerufen"), und die Regel, dass eine abgelehnte Entscheidung als SATZ
 * neben der Fläche steht und nicht als roter Fehler. Vier Kopien davon wären vier Meinungen
 * darüber, was ein 409 bedeutet — dieselbe Überlegung wie in `lib/neuabruf.ts`.
 *
 * Diese Datei entscheidet NICHTS: ob eine Auswahl überhaupt erscheint, sagen die reinen Regeln
 * (`trefferauswahl()` für Places, `registerTrefferauswahl()` für das Register). Hier steht nur der
 * Aufruf und die Übersetzung.
 */
import { apiFetch, toApiError, type ApiError } from "./api";

/** Der Datensatz, an dem die Registerzeile hängt (sie selbst hängt am Firmennamen, nicht an ihm). */
export type RegisterAnker = { kundeId: string } | { anfrageId: string };

/** Die Version der Zeile, auf die sich die Entscheidung bezieht (M-45). */
export interface ZeilenVersion {
  lookupId: string;
  lookedUpAt: string;
}

export interface ZuordnungAntwort {
  ok: boolean;
  /** Ein Satz für die Fläche — `null`, wenn alles geklappt hat. Nie ein Wurf. */
  hinweis: string | null;
}

/**
 * Der eine Satz für den einen Fall, der kein Fehler ist: zwischen Anzeigen und Klicken ist ein
 * neuer Abruf gelaufen. Steht hier, damit er an allen vier Stellen gleich lautet.
 */
const VERALTET =
  "Der Stand wurde inzwischen neu abgerufen – bitte die aktuelle Anzeige prüfen und erneut entscheiden.";

async function sende(pfad: string, koerper: unknown): Promise<ZuordnungAntwort> {
  try {
    const res = await apiFetch(pfad, { method: "PATCH", body: JSON.stringify(koerper) });
    if (res.ok) return { ok: true, hinweis: null };
    const fehler = (await toApiError(res)) as ApiError;
    if (res.status === 409) return { ok: false, hinweis: VERALTET };
    return { ok: false, hinweis: fehler.message };
  } catch (e) {
    return { ok: false, hinweis: e instanceof Error ? e.message : String(e) };
  }
}

/** Einer der mehrdeutigen Registertreffer wird zum Ergebnis. `index` ist die Position in der
 *  angezeigten Liste — die Version nagelt genau diese Liste fest (sql/245). */
export function registerTrefferWaehlen(anker: RegisterAnker, version: ZeilenVersion, index: number): Promise<ZuordnungAntwort> {
  return sende("/api/anreicherung/handelsregister/auswahl", { ...anker, ...version, trefferIndex: index });
}

/** „Keiner davon" bzw. „dieser Eintrag gehört nicht hierher" — dieselbe Tatsache, ein Endpunkt. */
export function registerErgebnisVerwerfen(anker: RegisterAnker, version: ZeilenVersion): Promise<ZuordnungAntwort> {
  return sende("/api/anreicherung/handelsregister/verwerfen", { ...anker, ...version });
}

/** Dasselbe für den Google-Vorschlag. Der Anker ist hier der Kunde selbst (die Zeile hängt an
 *  ihm, nicht am Firmennamen) — deshalb steht er im Pfad und nicht im Körper. */
export function placesErgebnisVerwerfen(kundeId: string, version: ZeilenVersion): Promise<ZuordnungAntwort> {
  return sende(`/api/customers/${kundeId}/places-lookup/verwerfen`, version);
}
